import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ThreeDot from "../common/ThreeDot";
import { useSelector } from "react-redux";
import { getAllOrders } from "../../services/order.service";
import Swal from "sweetalert2";
import ViewDetailsButton from "../common/ViewDetailsButton";
import { debounce } from "lodash";
import { getAllOrdersGraphQL } from "../../services/graphql.service";

export default function Orders() {
  const { token } = useSelector((state) => state?.auth);
  const [orders, setOrders] = useState(null);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(null);
  const itemsPerPage = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const fetchOrders = async (page = 1) => {
    try {
      const params = { page, retailer_order_list: "True" };
      const res = await getAllOrders(token, params);
      if (res && res.status >= 200 && res.status < 300) {
        setOrders(res?.data?.results?.orders);
        // console.log(res?.data);

        setNext(res?.data?.next);
        setPrevious(res?.data?.previous);
        setCurrentPage(page);
        setTotalEntries(res?.data?.count || null);
        setTotalPages(Math.ceil((res?.data?.count || 0) / itemsPerPage));
      } else {
        Swal.fire(
          "Error",
          res?.data?.message || "Failed to fetch orders. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  const fetchOrdersGraphQL = async (page = 1, search) => {
    console.log("Fetching orders:", { search, page });

    try {
      const res = await getAllOrdersGraphQL(token, page, search, "retailer");

      if (res && res.status >= 200 && res.status < 300) {
        console.log("Orders fetched successfully:", res.data);

        setOrders(res?.data?.data?.search_orders?.results);
        setNext(res?.data?.data?.search_orders?.next);
        setPrevious(res?.data?.data?.search_orders?.previous);
        setCurrentPage(page);
        setTotalEntries(res?.data?.data?.search_orders?.count || null);
        setTotalPages(
          Math.ceil((res?.data?.data?.search_orders?.count || 0) / itemsPerPage)
        );
      } else {
        Swal.fire(
          "Error",
          res?.data?.message || "Failed to fetch orders. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearchHandler(value);
  };

  const debouncedSearchHandler = useCallback(
    debounce((value) => {
      setDebouncedSearch(value);
      setCurrentPage(1);
    }, 300),
    []
  );

  useEffect(() => {
    if (debouncedSearch) {
      fetchOrdersGraphQL(currentPage, debouncedSearch);
    } else {
      fetchOrders(currentPage);
    }
  }, [debouncedSearch, currentPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const startEntry = (currentPage - 1) * itemsPerPage + 1;
  const endEntry = Math.min(currentPage * itemsPerPage, totalEntries);

  return (
    <div className="content-body">
      <div className="container-fluid">
        <div className="form-head d-flex mb-3 align-items-start justify-content-between">
          <div className="input-group search-area d-inline-flex">
            <input
              type="text"
              className="form-control"
              placeholder="Search here"
              onChange={(e) => handleSearchChange(e)}
            />
            <div className="input-group-append">
              <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span>
            </div>
          </div>
        </div>

        {/* Orders Table */}

        <div className="row">
          <div className="col-xl-12">
            {orders && orders.length > 0 ? (
              <div className="table-responsive">
                <div
                  id="example5_wrapper"
                  className="dataTables_wrapper no-footer"
                >
                  <table
                    id="example5"
                    className="display mb-4 dataTablesCard dataTable no-footer table order-request2"
                    role="grid"
                    aria-describedby="example5_info"
                  >
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Retailer</th>
                        <th>Address</th>
                        <th>Ordered On</th>
                        <th>Delivered On</th>
                        <th>Total</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders &&
                        orders?.length > 0 &&
                        orders?.map((item, i) => (
                          <tr key={i}>
                            <td>#{item?.order_id}</td>
                            <td>{item.retailer}</td>
                            <td>{item.address}</td>
                            <td>
                              <>
                                {new Date(item?.created_at)
                                  .toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })
                                  .replace(/(\b[A-Za-z]{3}\b)/, "$1,")}
                                <br />
                                {new Date(item?.created_at)
                                  .toLocaleTimeString("en-GB", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  })
                                  .toUpperCase()}
                              </>
                            </td>
                            <td>
                              {item?.delivered_at &&
                              item.status === "DELIVERED" ? (
                                <>
                                  {new Date(item?.delivered_at)
                                    .toLocaleDateString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })
                                    .replace(/(\b[A-Za-z]{3}\b)/, "$1,")}
                                  <br />
                                  {new Date(item?.delivered_at)
                                    .toLocaleTimeString("en-GB", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    })
                                    .toUpperCase()}
                                </>
                              ) : (
                                "NA"
                              )}
                            </td>
                            <td>
                              ₹
                              {(item.final_amount * 1)?.toLocaleString(
                                "en-IN",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <Link
                                  className={`btn ${
                                    item?.status === "SHIPPED"
                                      ? "btn-secondary"
                                      : item?.status === "CANCELLED"
                                      ? "btn-danger"
                                      : item?.status === "PENDING"
                                      ? "btn-warning"
                                      : item?.status === "DELIVERED"
                                      ? "btn-success"
                                      : "btn-info"
                                  } light btn-sm`}
                                >
                                  {item.status}
                                </Link>
                                <div className="dropdown ms-auto c-pointer">
                                  <ThreeDot />
                                  <div className="dropdown-menu dropdown-menu-end">
                                    <ViewDetailsButton
                                      to={`/orders/details`}
                                      id={item.order_id}
                                      tab={"details"}
                                    />
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="card-body">
                  <div className="py-5 my-5 text-center fs-4 fw-bold">
                    No Orders
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pagination Info */}
        {totalEntries && totalEntries > 10 && (
          <div className="dataTables_wrapper no-footer">
            <div className="dataTables_info">
              Showing {startEntry} to {endEntry} of {totalEntries} entries
            </div>
            <div className="dataTables_paginate paging_simple_numbers">
              <button
                className={`btn btn-primary me-2 ${!previous && "disabled"}`}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Previous
              </button>
              <span>
                {(() => {
                  const maxVisible = 7;
                  const pages = Array.from(
                    { length: totalPages },
                    (_, i) => i + 1
                  );
                  let start = Math.max(
                    currentPage - Math.floor(maxVisible / 2),
                    1
                  );
                  let end = start + maxVisible - 1;

                  if (end > totalPages) {
                    end = totalPages;
                    start = Math.max(end - maxVisible + 1, 1);
                  }

                  return pages.slice(start - 1, end).map((page) => (
                    <button
                      key={page}
                      className={`btn btn-sm mx-1 ${
                        currentPage === page ? "btn-dark" : "btn-outline-dark"
                      }`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  ));
                })()}
              </span>
              <button
                className={`btn btn-primary ms-2 ${!next && "disabled"}`}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
